import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, ISTEM_SABLONU_ANAHTARLARI, type IstemSablonuAnahtari, type Me, type PromptTemplate } from './api';
import { Alan, AlanSatiri, Notices } from './Workflow';
import { ORTAK, tamSayi, tarihSaat, TANIMSIZ } from './metin/ortak';
import { ISTEM_SABLONU_ADI, ISTEM_SABLONU_ISI, YAPILANDIRMA_METIN } from './metin/yapilandirma';

/**
 * NFR-04: istem şablonlarının yönetim ekranı.
 *
 * ## Neden ayrı bir sayfa
 *
 * Şablonu yazma yetkisi `config:write` ve onu yalnız sistem yöneticisi tutuyor. Üç aday vardı ve
 * üçü de yanlıştı:
 *
 *   - **Satış sayfası.** Şablonun etkisi orada görünüyor (talep ayrıştırma), ama satışçıda
 *     `config:write` yok. Bu deponun kuralı, basılamayacak düğmeyi hiç çizmemek; ekranı oraya
 *     koymak tam olarak o kuralı çiğnerdi.
 *   - **Ayarlar.** O sayfanın kendi vaadi yazılı: seçim tarayıcıda saklanıyor, hesaba yazılmıyor.
 *     Sunucuya yazılan ve **bütün kullanıcıları** etkileyen bir ayarı oraya koymak, sayfanın kendi
 *     cümlesini yalanlamak olurdu.
 *   - **Kullanıcılar.** Sistem yöneticisinin sayfası, ama adı ve içeriği çalışan profilleri. Bir
 *     istem şablonu bir kullanıcı değil.
 *
 * Bu yüzden dördüncü seçenek: kendi adresi olan bir yapılandırma sayfası, menüde yalnız
 * `config:write` tutana görünen. Sayfanın kapsamı bugünden belli — kod dağıtımı gerektirmeden
 * değişen, sunucuya yazılan çalışma zamanı ayarları; ikinci bir ayar geldiğinde yeri hazır.
 *
 * ## Ölçülen kusur
 *
 * `PromptTemplate` satırı tohumla gelmiyor. Boş bir veritabanında talep ayrıştırıcısı hiçbir
 * model çağrısı yapmıyor ve sessizce elle doldurma yoluna düşüyor; bunu düzeltmenin tek yolu bir
 * betikti (`ai:istem-yukle`) ve bir betik operatörün aracı değil. Üç uç da açıktı, ekranı yoktu.
 */
function IstemSablonlariBolumu({ yazabilir }: { yazabilir: boolean }) {
  const [sablonlar, setSablonlar] = useState<PromptTemplate[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bildirim, setBildirim] = useState<string | null>(null);
  const [acikGovde, setAcikGovde] = useState<string | null>(null);
  const [anahtar, setAnahtar] = useState<IstemSablonuAnahtari>('REQUEST_PARSING');
  const [govde, setGovde] = useState('');
  const [not, setNot] = useState('');
  const [hemenEtkin, setHemenEtkin] = useState(true);
  const metin = YAPILANDIRMA_METIN.istem;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setSablonlar(await api.promptTemplates());
    } catch (caught) {
      setHata(caught instanceof ApiError ? caught.message : metin.hata);
    }
  }, [metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const isle = async (eylem: () => Promise<string>) => {
    setHata(null);
    setBildirim(null);
    try {
      setBildirim(await eylem());
      await yukle();
    } catch (caught) {
      // Ucun adlandırdığı ret olduğu gibi yazılıyor; ekranın kendi doğrulaması da (uca hiç
      // gitmeyen kısa metin uyarısı) aynı yoldan geçiyor. Genel bir "bir şeyler ters gitti"
      // cümlesi yalnız gerçekten tanınmayan bir hata için kalıyor.
      setHata(caught instanceof Error ? caught.message : metin.hata);
    }
  };

  return (
    <section className="content-section" data-testid="prompt-templates" aria-labelledby="prompt-templates-title">
      <div className="section-heading">
        <div>
          <h2 id="prompt-templates-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{tamSayi(sablonlar?.length ?? 0)} {ORTAK.kayit}</span>
      </div>
      <Notices scope="prompt-templates" error={hata} notice={bildirim} />
      {yazabilir ? null : <p className="callout callout-warn" role="note">{metin.saltOkunur}</p>}

      {/*
        Her anahtar kendi bloğunda ve **etkin sürümü başlıkta**: asıl soru "hangi sürüm şu an
        konuşuyor", ve o soru bir tablonun içindeki bir satırda kaybolmamalı. Hiç sürümü olmayan
        anahtar da çiziliyor; boş bir bloğu hiç göstermemek, düzeltilmesi gereken durumu gizlerdi.
      */}
      {ISTEM_SABLONU_ANAHTARLARI.map((sablonAnahtari) => {
        const surumler = (sablonlar ?? []).filter((sablon) => sablon.key === sablonAnahtari);
        const etkin = surumler.find((sablon) => sablon.active);
        return (
          <div key={sablonAnahtari} data-testid={`prompt-template-${sablonAnahtari}`}>
            <h3>{ISTEM_SABLONU_ADI[sablonAnahtari] ?? sablonAnahtari}</h3>
            <p className="muted">{ISTEM_SABLONU_ISI[sablonAnahtari] ?? ''}</p>
            <p
              className={etkin ? 'callout callout-ok' : 'callout callout-warn'}
              role="status"
              data-testid={`prompt-template-active-${sablonAnahtari}`}
            >
              {etkin ? metin.etkinSurum(tamSayi(etkin.version)) : metin.etkinYok}
            </p>
            {surumler.length === 0 ? (
              <p className="muted">{metin.bos}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{metin.sutunSurum}</th>
                      <th>{metin.sutunDurum}</th>
                      <th>{metin.sutunNot}</th>
                      <th>{metin.sutunYazilma}</th>
                      <th>{metin.sutunUzunluk}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {surumler.map((sablon) => (
                      <tr key={sablon.id} data-testid="prompt-template-row">
                        <td>{tamSayi(sablon.version)}</td>
                        <td>{sablon.active ? metin.etkin : metin.pasif}</td>
                        {/*
                          Değişiklik notu **alıntı**: şablonu yazan kişinin cümlesi, ekranın kendi
                          cümlesi değil. `data-veri` bunu söylüyor, ve ekran kalitesi denetimi
                          (`onyuz-kalite.spec.ts`) alıntılanan metni kendi dil kuralına tabi
                          tutmuyor — ölçüldü: kayıtlı bir not iç sürüm numarası içeriyor ve o not
                          doğru, düzeltilecek bir ekran metni değil.
                        */}
                        <td data-veri>{sablon.changeNote ?? TANIMSIZ}</td>
                        <td>{tarihSaat(sablon.createdAt)}</td>
                        <td>{metin.karakter(tamSayi(sablon.body.length))}</td>
                        <td>
                          <button
                            type="button"
                            className="ikincil"
                            data-testid={`prompt-template-show-${sablon.id}`}
                            onClick={() => setAcikGovde((simdiki) => (simdiki === sablon.id ? null : sablon.id))}
                          >
                            {acikGovde === sablon.id ? metin.gizle : metin.goster}
                          </button>
                          {yazabilir && !sablon.active ? (
                            <button
                              type="button"
                              className="ikincil"
                              data-testid={`prompt-template-activate-${sablon.id}`}
                              onClick={() => void isle(async () => {
                                const etkinlesen = await api.activatePromptTemplate(sablon.id);
                                return metin.etkinlestirildi(tamSayi(etkinlesen.version));
                              })}
                            >
                              {metin.etkinlestir}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/*
              Şablon metni istendiğinde açılıyor. Her zaman açık olsaydı on bin karakterlik bir
              metin sayfayı yutardı; hiç gösterilmeseydi yazılan şeyi kimse doğrulayamazdı.
            */}
            {surumler
              .filter((sablon) => sablon.id === acikGovde)
              .map((sablon) => (
                <pre
                  key={sablon.id}
                  className="uzun-deger"
                  data-veri
                  data-testid={`prompt-template-body-${sablon.id}`}
                  /*
                    `pre` varsayılanı `white-space: pre`: uzun bir istem satırı sarmıyor ve dar
                    ekranda sayfayı yana kaydırıyordu. Sarma burada tek satır; yeni bir sınıf
                    açmak, tek kullanımı olan bir kural için ikinci bir yer demekti.
                  */
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {sablon.body}
                </pre>
              ))}
          </div>
        );
      })}

      {yazabilir ? (
        <>
          <h3>{metin.yeniBaslik}</h3>
          <AlanSatiri>
            <Alan etiket={metin.anahtarEtiketi} zorunlu>
              <select
                data-testid="prompt-template-key"
                value={anahtar}
                onChange={(olay) => setAnahtar(olay.target.value as IstemSablonuAnahtari)}
              >
                {ISTEM_SABLONU_ANAHTARLARI.map((deger) => (
                  <option key={deger} value={deger}>{ISTEM_SABLONU_ADI[deger] ?? deger}</option>
                ))}
              </select>
            </Alan>
            <Alan etiket={metin.notEtiketi} ipucu={metin.notIpucu}>
              <input data-testid="prompt-template-note" value={not} maxLength={300} onChange={(olay) => setNot(olay.target.value)} />
            </Alan>
          </AlanSatiri>
          <AlanSatiri>
            <Alan etiket={metin.govdeEtiketi} ipucu={metin.govdeIpucu} zorunlu>
              <textarea
                data-testid="prompt-template-body"
                rows={8}
                maxLength={16_000}
                value={govde}
                onChange={(olay) => setGovde(olay.target.value)}
              />
            </Alan>
          </AlanSatiri>
          <label>
            <input
              type="checkbox"
              data-testid="prompt-template-activate-now"
              checked={hemenEtkin}
              onChange={(olay) => setHemenEtkin(olay.target.checked)}
            />{' '}
            {metin.hemenEtkinEtiketi}
          </label>
          <p className="alan-ipucu">{metin.hemenEtkinIpucu}</p>
          <button
            type="button"
            className="birincil"
            data-testid="prompt-template-create"
            onClick={() => void isle(async () => {
              if (govde.trim().length < 20) throw new Error(metin.govdeUyarisi);
              const yazilan = await api.createPromptTemplate({
                key: anahtar,
                body: govde,
                active: hemenEtkin,
                ...(not.trim() ? { changeNote: not.trim() } : {}),
              });
              setGovde('');
              setNot('');
              return yazilan.active ? metin.yazildiVeEtkin(tamSayi(yazilan.version)) : metin.yazildi(tamSayi(yazilan.version));
            })}
          >
            {metin.yaz}
          </button>
        </>
      ) : null}
    </section>
  );
}

/**
 * Yapılandırma sayfası. Bugün tek bölümü var; sayfanın kapsamı bölümün kendisinden geniş
 * (sunucuya yazılan çalışma zamanı ayarları) ve ikinci bir ayar geldiğinde buraya bir bölüm daha
 * eklenecek.
 */
export function YapilandirmaSayfasi({ me }: { me: Me }) {
  const yazabilir = me.permissions.includes('config:write');
  const okuyabilir = me.permissions.includes('config:read');
  return (
    <div className="workflow-stack" data-testid="yapilandirma">
      <section className="content-section" aria-labelledby="yapilandirma-title">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{YAPILANDIRMA_METIN.ustBaslik}</span>
            <h2 id="yapilandirma-title">{YAPILANDIRMA_METIN.baslik}</h2>
            <p className="muted">{YAPILANDIRMA_METIN.tarif}</p>
          </div>
        </div>
      </section>
      {okuyabilir ? <IstemSablonlariBolumu yazabilir={yazabilir} /> : null}
    </div>
  );
}
